// Keeps an open calendar in step with availability changes made by other users, off the back of the
// PushEventService poll.
//
// This one cannot patch a single row the way the appointment and invoice services do, and it is
// worth being clear why rather than looking like an oversight:
//
//   - There is no single-availability endpoint. The API exposes the list, a create, an update and a
//     delete, so the only way to read one record back is to read them all for that clinician.
//   - One stored record is not one thing on screen. A recurring availability expands into an event
//     per occurrence, through transforms that read the whole list. Hand-patching the expansion would
//     drift from what a refresh draws.
//
// So the saving here is in deciding not to ask. Availability is fetched per clinician per practice,
// and a calendar shows one clinician at a time, so an event for anyone else - the common case in a
// practice with several clinicians - costs nothing. When the change is on screen, the reload is the
// same single request the calendar already makes when you switch clinician, and a burst of events is
// coalesced into one of them.
//
// A delete can still be free where the view holds the raw records: see removeLocally.
//
// The reload batching itself lives in LiveReloadService, which the task, waitlist and practice
// subscriptions share.
app.service("CalendarAvailabilityLiveUpdateService", [
	"LiveReloadService",
	"PUSH_EVENTS",
	function (LiveReloadService, PUSH_EVENTS) {
		/** The event's payload, under the names this service uses. */
		function toChange(payload) {
			return {
				availabilityId: payload.availabilityId,
				userId: payload.userId,
			};
		}

		/**
		 * Wires a view up to availability push events for the life of its scope.
		 *
		 * @param scope the view's scope; listeners are removed when it is destroyed
		 * @param spec  isInView(change) - whether the change belongs to the clinician, practice and
		 *                  filters currently on screen. This is the whole saving, so it should be as
		 *                  tight as the view's own getPracticeAvailability call.
		 *              reload() - re-fetch this clinician's availability and re-render
		 *              removeLocally(change) - optional. Drop the record from the view without a
		 *                  request, returning whether it did. Only worth supplying where the view holds
		 *                  the stored records themselves rather than an expansion of them; returning
		 *                  false falls back to reload().
		 */
		function subscribe(scope, spec) {
			LiveReloadService.subscribe(scope, {
				events: [
					PUSH_EVENTS.CALENDAR_AVAILABILITY_ADDED,
					PUSH_EVENTS.CALENDAR_AVAILABILITY_UPDATED,
					PUSH_EVENTS.CALENDAR_AVAILABILITY_DELETED,
				],
				isRelevant: function (payload) {
					const change = toChange(payload);
					return !!change.availabilityId && spec.isInView(change);
				},
				handleLocally: function (payload, pushEvent) {
					// A delete names everything needed to drop the record, so a view holding the records
					// themselves needs no request at all.
					if (pushEvent.type !== "CALENDAR_AVAILABILITY_DELETED" || !spec.removeLocally) {
						return false;
					}
					return spec.removeLocally(toChange(payload));
				},
				reload: function () {
					spec.reload();
				},
			});
		}

		const service = {
			subscribe: subscribe,

			/** Whether a change belongs to a given clinician. */
			isForUser: function (change, userId) {
				return LiveReloadService.sameId(change.userId, userId);
			},

			/**
			 * Drops every record with the changed availability's id out of a list of stored availability
			 * records, in place.
			 *
			 * @return whether anything was actually removed
			 */
			removeById: function (list, availabilityId) {
				if (!angular.isArray(list)) {
					return false;
				}
				let removed = false;
				for (let i = list.length - 1; i >= 0; i--) {
					if (LiveReloadService.sameId(list[i].availability_id, availabilityId)) {
						list.splice(i, 1);
						removed = true;
					}
				}
				return removed;
			},
		};

		return service;
	},
]);
